import { useTranslation } from 'react-i18next';

import type { ReactNode } from 'react';

import { ChangeStateLoader } from 'Common/ConnectToDesktopButtons/ConnectToDesktopBase/ChangeStateLoader';
import type { CourseModel } from 'Courses/data';
import {
  useSelectIsFreshStartDesktopStartPending,
  useSelectIsMyDesktopLoading,
  useSelectIsMyDesktopStartPending,
  useSelectIsTrainerDesktopStartPending
} from 'Courses/data';

import { StyledIFrame, StyledWrapper } from './live-preview.styles';
import { MachineInfo } from './machine-info';

import type { DesktopModel } from 'Api';

type Props = {
  fullscreenModeIsEnabled?: boolean;
  settingsOpened?: boolean;
  course: CourseModel;
  desktop: DesktopModel;
  isParticipantDesktop: boolean;
  isTrainerDesktop: boolean;
  isTrainerDesktopPreview: boolean;
  connectToDesktopButton: ReactNode;
  participantsMachineControlActive?: boolean;
};

export const LivePreview = ({
  fullscreenModeIsEnabled,
  settingsOpened,
  course,
  desktop,
  isParticipantDesktop,
  isTrainerDesktop,
  isTrainerDesktopPreview,
  connectToDesktopButton,
  participantsMachineControlActive = false
}: Readonly<Props>) => {
  const { t } = useTranslation();

  const isMyDesktopLoading = useSelectIsMyDesktopLoading(course.id);
  const isMyDesktopStartPending = useSelectIsMyDesktopStartPending(course.id);
  const isTrainerDesktopStartPending = useSelectIsTrainerDesktopStartPending(course.id);
  const isFreshStartDesktopStartPending = useSelectIsFreshStartDesktopStartPending(course.id);

  let isCurrentDesktopStartPending = false;
  if (isTrainerDesktop) {
    isCurrentDesktopStartPending = course.isTrainer
      ? isMyDesktopStartPending
      : isTrainerDesktopStartPending;
  } else if (isParticipantDesktop) {
    isCurrentDesktopStartPending = isMyDesktopStartPending;
  } else {
    isCurrentDesktopStartPending = isFreshStartDesktopStartPending;
  }

  let componentToRender: ReactNode;

  if (isMyDesktopLoading) {
    componentToRender = '';
  } else if (desktop?.isRunning && desktop?.instanceId && !isCurrentDesktopStartPending) {
    let linkToUse: string;
    if (isParticipantDesktop && participantsMachineControlActive) {
      linkToUse = desktop?.link as string;
    } else if (isParticipantDesktop || isTrainerDesktopPreview) {
      linkToUse = desktop?.viewLink as string;
    } else {
      linkToUse = desktop?.link as string;
    }

    componentToRender = (
      <StyledIFrame
        key={`${desktop?.instanceId}-${linkToUse}`}
        src={linkToUse}
        fullscreenModeIsEnabled={fullscreenModeIsEnabled}
        settingsOpened={settingsOpened}
        participantsMachineControlActive={participantsMachineControlActive}
      />
    );
  } else if (
    !desktop?.isRunning &&
    ((course.hasFreshStart && course.isFreshStartMachineReadyToClone) ||
      desktop?.instanceId ||
      fullscreenModeIsEnabled) &&
    !isTrainerDesktopPreview
  ) {
    componentToRender = connectToDesktopButton;
  } else {
    componentToRender = isCurrentDesktopStartPending ? (
      <ChangeStateLoader text={t('Connecting')} />
    ) : (
      <MachineInfo
        courseId={course.id}
        isTrainer={course.isTrainer!}
        isTrainerDesktopPreview={isTrainerDesktopPreview}
        isCourseCompleted={!!(course.flags?.isEnded ?? course.flags?.isFinished)}
      />
    );
  }

  return (
    <StyledWrapper fullscreenModeIsEnabled={fullscreenModeIsEnabled}>
      {componentToRender}
    </StyledWrapper>
  );
};
